import type { CustomizationSearchFilters } from '@lumapps/customizations/types';
import { isArrayFacet, convertArrayParameterToValues } from '@lumapps/router/utils';

import { CUSTOMIZATION_FILTERS_FACET_MAP, FACET_PREFIX } from '../constants';

interface FilterOption {
    name: string;
    values: string[] | string;
    kind: 'array' | 'string';
}

interface MergeResult {
    filters: FilterOption[];
    mergedCustomizationFilters: Set<string>;
}

/**
 * Converts facet value to array of strings
 * @param facetValue - Facet value (can be array format like "(id1,id2)", comma-separated "id1,id2", or single value)
 * @returns Array of values
 */
const convertToArray = (facetValue: string): string[] => {
    // Check if it's in array format with parentheses: (id1,id2)
    if (isArrayFacet(facetValue)) {
        return convertArrayParameterToValues(facetValue);
    }

    // Check if it's comma-separated without parentheses: id1,id2
    if (facetValue.includes(',')) {
        return facetValue.split(',');
    }

    // Single value
    return [facetValue];
};

/**
 * Merges user facet values with customization filter values
 * @param userValues - User facet values as array
 * @param customValues - Customization filter values to merge
 * @returns Merged and deduplicated array of values
 */
export const mergeFilterValues = (userValues: string[], customValues: string[]): string[] =>
    // Merge and deduplicate
    [...new Set([...customValues, ...userValues])];

/**
 * Processes user facets and merges them with customization filters where applicable
 * @param facets - User-selected facets from URL parameters
 * @param customizationFilters - Customization filters from global store
 * @returns Object containing processed filters and set of merged customization filter names
 */
export const processUserFacets = (
    facets: Record<string, string>,
    customizationFilters?: CustomizationSearchFilters,
): MergeResult => {
    const filters: FilterOption[] = [];
    const mergedCustomizationFilters = new Set<string>();

    Object.keys(facets).forEach((key) => {
        const facetValue = facets[key];
        const filterName = key.replace(FACET_PREFIX, '');
        const isArrayFacetValue = isArrayFacet(facetValue);

        // Check if this facet has corresponding customization filters that need to be merged
        let shouldMergeCustomization = false;
        let customValuesToMerge: string[] = [];

        if (customizationFilters) {
            Object.entries(CUSTOMIZATION_FILTERS_FACET_MAP).forEach(([customFilterName, facetId]) => {
                const customValues = customizationFilters[customFilterName as keyof CustomizationSearchFilters];

                if (filterName === facetId && customValues && Array.isArray(customValues) && customValues.length > 0) {
                    shouldMergeCustomization = true;
                    customValuesToMerge = customValues;
                    mergedCustomizationFilters.add(customFilterName);
                }
            });
        }

        // Determine final values and kind based on whether we're merging
        if (shouldMergeCustomization || isArrayFacetValue) {
            // Use array format when:
            // 1. Merging with customization filters (forces array)
            // 2. Original facet value is already an array
            const userValues = convertToArray(facetValue);
            const finalValues = shouldMergeCustomization
                ? mergeFilterValues(userValues, customValuesToMerge)
                : userValues;

            filters.push({
                name: filterName,
                values: finalValues,
                kind: 'array',
            });
        } else {
            // Preserve original string format for single-value facets
            filters.push({
                name: filterName,
                values: facetValue,
                kind: 'string',
            });
        }
    });

    return { filters, mergedCustomizationFilters };
};

/**
 * Adds customization filters that weren't merged with user facets
 * @param customizationFilters - Customization filters from global store
 * @param mergedCustomizationFilters - Set of filter names that were already merged
 * @returns Array of filter options for non-merged customization filters
 */
export const addNonMergedCustomizationFilters = (
    customizationFilters: CustomizationSearchFilters,
    mergedCustomizationFilters: Set<string>,
): FilterOption[] => {
    const filters: FilterOption[] = [];

    Object.entries(CUSTOMIZATION_FILTERS_FACET_MAP).forEach(([customFilterName, facetId]) => {
        const customValues = customizationFilters[customFilterName as keyof CustomizationSearchFilters];

        if (
            !mergedCustomizationFilters.has(customFilterName) &&
            customValues &&
            Array.isArray(customValues) &&
            customValues.length > 0
        ) {
            filters.push({
                name: facetId,
                values: customValues,
                kind: 'array',
            });
        }
    });

    return filters;
};
